import React, { createContext, useContext, useState, useCallback } from 'react';
import { useAuth } from './authContext';

const PrlocalBusinessDataContext = createContext();

export const usePrlocalBusinessData = () => {
  const context = useContext(PrlocalBusinessDataContext);
  if (!context) {
    throw new Error('usePrlocalBusinessData must be used within a PrlocalBusinessDataProvider');
  }
  return context;
};

export const PrlocalBusinessDataProvider = ({ children }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { makeAuthenticatedRequest, loginType } = useAuth();

  // API base URL
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://7d515f15e81f.ngrok-free.app';
  
  // Determine the correct base URL based on login type
  // Routes defined in main.go: /admin/prlocal-business-data/... or /master/prlocal-business-data/...
  const getBaseUrl = useCallback(() => {
    if (loginType === 'master') {
      return `${API_BASE_URL}/master/prlocal-business-data/api/v1/business-data`;
    } else if (loginType === 'admin') {
      return `${API_BASE_URL}/admin/prlocal-business-data/api/v1/business-data`;
    } else {
      throw new Error('Invalid login type for PrLocal business data access');
    }
  }, [API_BASE_URL, loginType]);

  // Helper function to handle API responses
  const handleApiResponse = async (response) => {
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error || data.message || 'Request failed');
    }
    
    return data;
  };

  // Clear error state
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Validate user access
  const validateAccess = useCallback(() => {
    if (loginType !== 'admin' && loginType !== 'master') {
      throw new Error('Only admin and master users can access this data');
    }
  }, [loginType]);

  // ---------------------------------------------------------------------------
  // CORE API METHODS
  // ---------------------------------------------------------------------------

  // 1. Get All Businesses (Paginated)
  // Maps to: GET /api/v1/business-data/?limit=X&offset=Y&category=Z
  const getBusinesses = useCallback(async (options = {}) => {
    validateAccess();
    setLoading(true);
    setError(null);

    try {
      const queryParams = new URLSearchParams();
      
      if (options.limit) queryParams.append('limit', options.limit);
      if (options.offset) queryParams.append('offset', options.offset);
      if (options.category) queryParams.append('category', options.category);

      const baseUrl = getBaseUrl();
      const url = `${baseUrl}/`;
      const fullUrl = queryParams.toString() ? `${url}?${queryParams.toString()}` : url;
      
      const response = await makeAuthenticatedRequest(fullUrl);
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to fetch businesses';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getBaseUrl]);

  // 2. Get Business by Internal ID
  // Maps to: GET /api/v1/business-data/{id}
  const getBusinessById = useCallback(async (id) => {
    validateAccess();
    
    if (!id) throw new Error('Business ID is required');

    setLoading(true);
    setError(null);

    try {
      const baseUrl = getBaseUrl();
      const response = await makeAuthenticatedRequest(`${baseUrl}/${id}`);
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to get business details';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getBaseUrl]);

  // 3. Get Profile ID by Business ID (The new specific requirement)
  // Maps to: GET /api/v1/business-data/{id}/profile-id
  const getProfileIdByBusinessId = useCallback(async (id) => {
    validateAccess();
    
    if (!id) throw new Error('Business ID is required');

    setLoading(true);
    setError(null);

    try {
      const baseUrl = getBaseUrl();
      const response = await makeAuthenticatedRequest(`${baseUrl}/${id}/profile-id`);
      const result = await handleApiResponse(response);
      // Returns: { business_id: "...", profile_id: "..." }
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to retrieve profile ID';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getBaseUrl]);

  // ---------------------------------------------------------------------------
  // UTILITY & HELPER METHODS
  // ---------------------------------------------------------------------------

  // Health check for the service
  const checkHealth = useCallback(async () => {
    validateAccess();
    try {
      const healthUrl = loginType === 'master' 
        ? `${API_BASE_URL}/master/prlocal-business-data/health`
        : `${API_BASE_URL}/admin/prlocal-business-data/health`;
      
      const response = await makeAuthenticatedRequest(healthUrl);
      return await handleApiResponse(response);
    } catch (err) {
      console.error('PrLocal Data Context Health Check Failed:', err);
      throw err;
    }
  }, [makeAuthenticatedRequest, validateAccess, API_BASE_URL, loginType]);

  // Format business data for display (converting timestamps, handling nulls)
  const formatBusinessData = useCallback((business) => {
    if (!business) return null;

    return {
      ...business,
      // Format dates
      registrationDateFormatted: new Date(business.registration_date).toLocaleDateString(),
      createdAtFormatted: new Date(business.business_created_at).toLocaleString(),
      
      // Format booleans to readable text
      termsAccepted: business.business_terms ? 'Yes' : 'No',
      emailVerified: business.verified_email ? 'Verified' : 'Unverified',
      phoneVerified: business.verified_phone ? 'Verified' : 'Unverified',
      
      // Handle optional fields/nulls
      categoryDisplay: business.business_category || 'Uncategorized',
      deviceDisplay: business.device_type || 'Unknown',
      referralDisplay: business.referral_source || 'None',
      
      // Status indicators (for UI chips/badges)
      statusColor: business.verified_email ? 'green' : 'orange',
    };
  }, []);

  // Export business data to CSV format (helper for admin dashboard)
  const exportBusinessData = useCallback(async (filters = {}) => {
    validateAccess();
    try {
      // Fetch a larger set for export
      const exportFilters = { ...filters, limit: 1000, offset: 0 };
      const result = await getBusinesses(exportFilters);
      
      const csvRows = [
        ['ID', 'Name', 'Owner', 'Email', 'Phone', 'Category', 'Registered Date', 'Profile ID (Fetched Separately)']
      ];

      result.businesses.forEach(b => {
        csvRows.push([
          b.id,
          `"${b.business_name}"`, // Quote to handle commas in names
          `"${b.business_owner}"`,
          b.business_email,
          b.business_phone,
          b.business_category || '',
          new Date(b.registration_date).toLocaleDateString(),
          'N/A' 
        ]);
      });

      return {
        data: csvRows,
        count: result.count,
        filename: `prlocal_businesses_${new Date().toISOString().split('T')[0]}.csv`
      };
    } catch (err) {
      setError('Export failed: ' + err.message);
      throw err;
    }
  }, [getBusinesses, validateAccess]);

  // Debug info
  const getEndpointInfo = useCallback(() => {
    return {
      loginType,
      baseUrl: getBaseUrl(),
      accessLevel: loginType === 'master' ? 'Master Access' : 'Admin Access',
    };
  }, [loginType, getBaseUrl]);

  const value = {
    // State
    loading,
    error,
    clearError,

    // API Methods
    getBusinesses,
    getBusinessById,
    getProfileIdByBusinessId, // <--- The specific requirement

    // Utilities
    checkHealth,
    formatBusinessData,
    exportBusinessData,
    getEndpointInfo,
    
    // Computed Props
    isAdminUser: loginType === 'admin',
    isMasterUser: loginType === 'master',
  };

  return (
    <PrlocalBusinessDataContext.Provider value={value}>
      {children}
    </PrlocalBusinessDataContext.Provider>
  );
};